/**
 *
 * This file is part of Filespin.io (https://filespin.io)
 *
 **/

import { useAccessToken } from 'src/hooks/useAccessToken';
import useRoleManager from 'src/hooks/useRoleManager';
import { useImageEditAvailable } from 'src/hooks/useImageEditAvailable';
import { usePrintExportAvailable } from 'src/hooks/usePrintExportAvailable';
import { useFindDuplicates } from 'src/hooks/useFindDuplicates';
import useModal from 'src/hooks/useModal';
import VersionBadge from 'src/components/VersionBadge';
import { AssetVersionsSummary } from 'src/types/TAssets';
import PrintExportDialog from 'src/pages/Asset/components/PrintExportDialog';
import { deleteAsset, getAssetMediaUrl, restoreAsset } from 'src/api/functions/assetsApi';
import Checkbox from '../Checkbox';
import Dropdown from '../Dropdown';
import ConfirmDialog from '../ConfirmDialog';
import {
  PlayBox,
  FileArrowDown,
  TrashSimple,
  Gear,
  Pencil,
  Stack,
  WarningCircle,
  ChatAI,
  DotsThreeVertical,
  Printer,
  AssetGetLink,
  ImageSquare,
  Copy,
} from 'src/common/icons';
import AssetLinkDialog from 'src/components/AssetLinkDialog/AssetLinkDialog';
import ChangeThumbnailDialog from 'src/components/ChangeThumbnailDialog/ChangeThumbnailDialog';
import { isPreviewPlayable } from 'src/utils/previews';
import { humanFileSize } from 'src/utils/transform';
import styles from './ListingRow.module.scss';
import SvgArrowCounterClockwise from 'src/common/icons/ArrowCounterClockwise';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import { deletedAssetsAtom } from 'src/shared/deletedAssets';
import AssetPreview from '../AssetPreview';
import { useCollectionMutation } from 'src/api/mutations/collectionMutations';
import { useCollectionQuery } from 'src/api/queries/collectionsQueries';
import { queryNames } from 'src/api/utils/queryNames';
import { CollectionResponse } from 'src/types/TCollection';

import * as React from 'react';
import { useAuthUser } from 'react-auth-kit';
import { Link, useHistory, useLocation } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { useQueryClient } from 'react-query';
import cn from 'classnames';
import { detectExtension } from 'mimetypes';
import { useAtom } from 'jotai';
import { addCacheBuster } from 'src/utils/previews';

interface ListingRowProps {
  // Style class name
  className?: string;

  // Asset id
  id: string;

  // Siblings
  siblings?: string[];

  // Image url for the asset
  imageUrl: string;

  // Name of the asset
  name: string;

  // Size of the asset
  size: string;

  // Upload time of the asset
  uploadTime: string;

  // Content type of the asset (e.g. image/jpeg)
  contentType: string;

  // Is asset trashed
  trashed: boolean;

  // Status of the asset
  status: string;

  // Is asset selected
  isSelected?: boolean;

  // Is the asset a system-generated derivative (has _filespin_original_asset_id)
  isGenerated?: boolean;

  // For generated assets: id of the source asset they were derived from.
  originalAssetId?: string;

  // Content versioning summary from the search result (`versions`)
  versions?: AssetVersionsSummary | null;

  error?: any;

  // On select change handler
  onSelectChange?: React.ChangeEventHandler<HTMLInputElement> & ((value: React.ChangeEvent<HTMLInputElement>) => void);
}

/**
 *
 * @name ListingRow
 *
 *
 * @param {string} className Style class name
 * @param {string} id Asset id
 * @param {string[]} siblings
 * @param {string} imageUrl
 * @param {string} name
 * @param {string} size
 * @param {string} uploadTime
 * @param {string} contentType
 * @param {boolean} trashed
 * @param {string} status
 * @param {boolean} isSelected
 * @param {object} error
 * @param onSelectChange
 * @param rest
 *
 * @returns JSX Element
 *
 *
 * @description
 *
 * This component is used to render a row in the listing view. It is used in the
 * Assets and Collections pages. It is also used in the Trash page.
 *
 *
 **/
export const ListingRow: React.FC<ListingRowProps> = ({
  className,
  id,
  siblings,
  imageUrl,
  name,
  size,
  trashed,
  uploadTime,
  contentType,
  isSelected,
  isGenerated,
  originalAssetId,
  versions,
  onSelectChange,
  status,
  error,
  ...rest
}) => {
  const location = useLocation();

  // Add cache buster to imageUrl to ensure fresh thumbnails
  const cachedImageUrl = React.useMemo(() => addCacheBuster(imageUrl), [imageUrl]);

  return (
    <div className={cn([styles.ListingRow, className])}>
      <div className={styles.checkbox}>
        <Checkbox checked={isSelected} onChange={onSelectChange} />
      </div>

      {!trashed ? (
        <Link to={{ pathname: `/asset/${id}`, state: { background: location, siblings } }}>
          <div className={styles.thumbWrapper}>
            <AssetPreview src={cachedImageUrl} name={name} status={status} contentType={contentType} />
            {contentType && status !== 'NOT_READY' && isPreviewPlayable(contentType) && (
              <div className={styles.playbox}>
                <PlayBox />
              </div>
            )}
            {status !== 'NOT_READY' && <VersionBadge versions={versions} compact />}
            {isGenerated && status !== 'NOT_READY' && (
              <span
                className={styles.generatedBadge}
                data-tip="Generated asset"
                aria-label="Generated asset"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z" />
                  <path d="M19 14l.9 2.7L22 18l-2.1.6L19 21l-.9-2.4L16 18l2.1-1L19 14z" opacity="0.85" />
                </svg>
                <ReactTooltip effect="solid" />
              </span>
            )}
          </div>
        </Link>
      ) : (
        <div className={styles.thumbWrapper}>
          <AssetPreview src={cachedImageUrl} name={name} status={status} contentType={contentType} />
          {contentType && status !== 'NOT_READY' && isPreviewPlayable(contentType) && (
            <div className={styles.playbox}>
              <PlayBox />
            </div>
          )}
        </div>
      )}

      <div className={styles.title}>
        <div className={styles.fileNameContainer}>
          {status === 'NOT_READY' && (
            <div className={styles.processingAsset}>
              <Gear /> <span>Processing</span>
            </div>
          )}
          {error?.original && (
            <button data-tip={error?.original} className={styles.errorAsset}>
              <WarningCircle /> <span>ERROR</span>
              <ReactTooltip effect="solid" />
            </button>
          )}
          {!trashed ? (
            <Link
              className={styles.fileName}
              to={{
                pathname: `/asset/${id}`,
                state: { background: location, siblings },
              }}
            >
              {name}
            </Link>
          ) : (
            <span className={styles.fileName}>{name}</span>
          )}
        </div>
        <small>
          {id}
          <br />
          {detectExtension(contentType).toUpperCase()} {humanFileSize(Number(size))}
        </small>
      </div>

      <div>{new Date(uploadTime).toLocaleString()}</div>
      <ToolBox
        id={id}
        name={name}
        trashed={trashed}
        contentType={contentType}
        siblings={siblings}
        originalAssetId={isGenerated ? originalAssetId : undefined}
      />
    </div>
  );
};

interface ToolBoxProps {
  id: string;
  name: string;
  trashed: boolean;
  contentType?: string;
  siblings?: string[];
  originalAssetId?: string;
}

const ToolBox = React.memo((props: ToolBoxProps) => {
  const history = useHistory();
  const findDuplicates = useFindDuplicates();
  const downloadingRef = React.useRef('');
  const accessToken = useAccessToken();
  const auth = useAuthUser();
  const [deletedAssets, updateDeletedAssets] = useAtom(deletedAssetsAtom);
  const [isDeletingAsset, setIsDeletingAsset] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [isRestoringAsset, setIsRestoringAsset] = React.useState(false);
  const location = useLocation();

  const { showSuccess, showAlert } = useAlerts();

  const { getErrorMessage } = useErrorMessages();

  const { isUser } = useRoleManager();

  const imageEditAvailable = useImageEditAvailable();

  const printExportAvailable = usePrintExportAvailable();

  const { showModal } = useModal();

  const queryClient = useQueryClient();

  const { data: basketData } = useCollectionQuery(undefined, {
    staleTime: 1000 * 60 * 5,
  });

  const basketMutation = useCollectionMutation(Number(basketData?.data?.id), {
    onError: (error, newData, context: any) => {
      queryClient.setQueryData([queryNames.collection, null], context.previousData);

      showAlert(getErrorMessage(error.message));
    },
    onMutate: (data) => {
      /*showInfo(`Requesting update...`);*/
      const previousData = queryClient.getQueryData<CollectionResponse>([queryNames.collection, null]);
      queryClient.setQueryData<CollectionResponse | undefined>(
        [queryNames.collection, null],
        (old: CollectionResponse | undefined) => {
          if (typeof old !== 'undefined') {
            old.data.assets = (old?.data?.assets ?? [])
              .filter((id) => !(data?.deletions ?? []).includes(id))
              .concat(data?.additions ?? []);
          }
          return old;
        },
      );
      return { previousData };
    },
    onSuccess: () => {
      showSuccess('Successfully added asset to basket');
    },
  });

  const handleEditAsset = () => history.push(`/asset/${props.id}`, { background: location, sublings: props.siblings });

  const handleAIImageEdit = () =>
    history.push(`/asset/${props.id}`, { background: location, siblings: props.siblings, openAIEdit: true });

  const handlePrintExport = () => showModal(<PrintExportDialog assetId={props.id} assetName={props.name} />);

  const isImage = props.contentType?.startsWith('image/');

  const getMediaUrl = async () => {
    if (downloadingRef.current === '') {
      const url = await getAssetMediaUrl(props.id, 'original', auth()!.accessId, 'download', accessToken);
      downloadingRef.current = url;
      window.open(url);
    } else {
      window.open(downloadingRef.current);
    }
  };

  const addToBasket = () => {
    if (basketMutation.isLoading) return;
    basketMutation.mutateAsync({ additions: [props.id] });
  };

  const handleRestoreAsset = async () => {
    if (isRestoringAsset) return;

    setIsRestoringAsset(true);
    try {
      const response = await restoreAsset(props.id, accessToken);

      if (typeof response.status === 'number' && ![200, 202].includes(response.status)) {
        throw response;
      }

      showSuccess(`Successfully restored asset.`);
    } catch (e: any) {
      showAlert(getErrorMessage(e.message));
    } finally {
      setIsRestoringAsset(false);
    }
  };

  const handleDeleteAsset = async () => {
    if (isDeletingAsset) return;

    setIsDeletingAsset(true);

    try {
      const response = await deleteAsset(props.id, accessToken);
      if (typeof response.status === 'number' && ![200, 202].includes(response.status)) {
        throw response;
      }

      showSuccess(`Successfully deleted asset.`);
      updateDeletedAssets([...deletedAssets, props.id]);
    } catch (e: any) {
      //console.log(e);
      showAlert(getErrorMessage(e.message));
    } finally {
      setIsDeletingAsset(false);
    }
  };

  // Secondary actions live in a kebab menu; only the high-frequency
  // actions (Edit, Download, Add to basket) stay as visible icon buttons.
  // "Find duplicates" only reads, so the USER role gets it too; every other
  // kebab option is editor-only.
  const menuOptions = React.useMemo(() => {
    const opts: Array<{ key: string; value: React.ReactNode; icon?: React.ReactNode; datatest?: string }> = [];
    if (props.trashed) return opts;
    opts.push({ key: 'findDuplicates', value: 'Find duplicates', icon: <Copy />, datatest: 'find-duplicates-button' });
    if (isUser) return opts;
    if (isImage && imageEditAvailable) {
      opts.push({ key: 'aiImageEdit', value: 'AI Image Edit', icon: <ChatAI />, datatest: 'ai-image-edit-button' });
    }
    if (isImage && printExportAvailable) {
      opts.push({ key: 'printExport', value: 'Export for Print', icon: <Printer />, datatest: 'print-export-button' });
    }
    opts.push({ key: 'changeThumbnail', value: 'Change thumbnail', icon: <ImageSquare /> });
    opts.push({ key: 'delete', value: 'Delete', icon: <TrashSimple /> });
    return opts;
  }, [props.trashed, isUser, isImage, imageEditAvailable, printExportAvailable]);

  const handleMenuClick = (key: string) => {
    switch (key) {
      case 'aiImageEdit':
        handleAIImageEdit();
        break;
      case 'printExport':
        handlePrintExport();
        break;
      case 'findDuplicates':
        findDuplicates({ id: props.id, name: props.name });
        break;
      case 'changeThumbnail':
        showModal(<ChangeThumbnailDialog assetId={props.id} />);
        break;
      case 'delete':
        setShowDeleteConfirm(true);
        break;
      default:
        break;
    }
  };

  return (
    <>
    <div className={styles.actions}>
      {!isUser && !props.trashed && (
        <button data-tip="Edit Asset" onClick={handleEditAsset} data-test="click-asset-button">
          <Pencil />
          <ReactTooltip effect="solid" />
        </button>
      )}

      {!props.trashed && (
        <button data-tip="Download" onClick={getMediaUrl}>
          <FileArrowDown />
          <ReactTooltip effect="solid" />
        </button>
      )}

      {!props.trashed && (
        <button
          data-tip="Get link / Embed"
          onClick={() => showModal(<AssetLinkDialog assetId={props.id} assetName={props.name} />)}
          data-test="get-link-button"
        >
          <AssetGetLink />
          <ReactTooltip effect="solid" />
        </button>
      )}

      {!props.trashed && (
        <button data-tip="Add to basket" onClick={addToBasket} data-test="add-to-basket-button">
          <Stack />
          <ReactTooltip effect="solid" />
        </button>
      )}

      {!isUser && props.trashed && (
        <button data-tip="Restore Asset" onClick={handleRestoreAsset}>
          <SvgArrowCounterClockwise />
          <ReactTooltip effect="solid" />
        </button>
      )}

      {menuOptions.length > 0 && (
        <div className={styles.menu}>
          <Dropdown
            variant="menu"
            dropdownAnchor="right"
            icon={<DotsThreeVertical />}
            options={menuOptions}
            onClick={handleMenuClick}
          />
        </div>
      )}
    </div>

    {/* Outside `.actions` so its `button` styling doesn't bleed into the
        dialog; the dialog itself is position:fixed, so layout is unaffected. */}
    {showDeleteConfirm && (
      <ConfirmDialog
        title="Delete this asset?"
        message="The asset will be moved to trash and can be restored from there."
        onConfirm={() => {
          setShowDeleteConfirm(false);
          handleDeleteAsset();
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    )}
    </>
  );
});

export default ListingRow;
